// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { useParams } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ItemChangedReason } from 'dnd-kit-sortable-tree/dist/types';
import { useEffect, useState } from 'react';
import { SortableTree, TreeItems } from 'dnd-kit-sortable-tree';
import { Box, Button, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
// Components, Layouts, Pages
import { ItemGroupOrder } from '~/components/module/order';
// Others
import GroupContext from '~/context/groupContext/GroupContext';
import { ITreeItem } from '~/utils/interface/common';
import { DEFAULT_INDEX, EMPTY_STRING } from '~/utils/constants/common';
import { IFormOrder, IOrderGroup, IOrderItem } from '~/utils/interface/order';
import { DiscountTypeEnum, TypeListInventory } from '~/utils/enum/common';
import { IProductGroup, IQuoteAddOn, IQuoteListItem } from '~/utils/interface/quotes';
import { formatCurrencyUSD, handleCalculateDiscountedPrice } from '~/utils/helper/common';
// Styles, images, icons
import styles from './GroupsOrderForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const GroupsOrderForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const { id: orderId } = useParams();

  const {
    setValue,
    watch,
    formState: {},
  } = useFormContext<IFormOrder>();

  const {
    laborCost,
    productSubtotal,
    discountRate,
    listOrderItems,
    listOrderItemsSelected,
    selectGroupOrder,
    selectGroupItem,
    selectIdProductOrder,
    selectIdProductGroup,
    selectIdVariantOrder,
    selectIdVariantGroup,
    orderGroups,
    pricingTotal,
    templateOrderId,
    orderItems,
    orderAddOns,
    deleteItemIds,
  } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [items, setItems] = useState<TreeItems<ITreeItem<IProductGroup>>>([]);
  const [indexGroup, setIndexGroup] = useState<number>(DEFAULT_INDEX);

  const [isCheckDetail, setIsCheckDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!orderId || isCheckDetail) return;

    // Handle Set Data Group Detail
    const itemsData = handleConvertOrderDataDetailToTreeItem(orderGroups || [], orderItems || [], orderAddOns || []);

    if (itemsData?.length > 0) {
      setItems(itemsData);
      setIsCheckDetail(true);
    }
  }, [
    orderId,
    JSON.stringify(listOrderItems),
    JSON.stringify(orderGroups),
    JSON.stringify(orderItems),
    JSON.stringify(orderAddOns),
  ]);

  useEffect(() => {
    if (!templateOrderId) return;

    const itemsData = handleConvertOrderDataDetailToTreeItem(orderGroups || [], orderItems || [], orderAddOns || []);

    if (itemsData?.length > 0) {
      setItems(itemsData);
      setIsCheckDetail(true);
    }
  }, [templateOrderId]);

  useEffect(() => {
    const dataOrderGroup = handleGetListDataOrderGroups(items);
    const dataOrderItems = handleGetListDataOrderItems(items);
    const dataOrderAddOns = handleGetListDataOrderItemsAddOn(items);
    const newProductSubtotal = handleCalculateTotal(items);

    setValue('orderGroups', dataOrderGroup);
    setValue('orderItems', dataOrderItems);
    setValue('orderAddOns', dataOrderAddOns);
    setValue('productSubtotal', newProductSubtotal);

    setValue(
      'laborCost',
      dataOrderItems?.reduce<number>((sum, item) => sum + (Number(item?.laborCost) || 0), 0) +
        dataOrderAddOns?.reduce<number>((sum, item) => sum + (Number(item?.laborCost) || 0), 0)
    );
  }, [JSON.stringify(items), pricingTotal]);

  useEffect(() => {
    if (!listOrderItems) return;

    const selectedId = selectIdProductGroup || selectIdProductOrder;
    const selectedVariantId = selectIdVariantGroup || selectIdVariantOrder;

    const itemSelected = listOrderItems?.find(
      (item) =>
        item?.id === selectedId && String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
    );

    const isExist = listOrderItemsSelected?.some(
      (item) =>
        item?.id === selectedId && String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
    );

    if (!isExist && itemSelected) {
      setValue('listOrderItemsSelected', [...(listOrderItemsSelected || []), itemSelected]);
    }

    if (
      !selectedId ||
      !listOrderItems?.some(
        (item) =>
          item?.id === selectedId &&
          String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
      )
    ) {
      return;
    }

    const newItems = handleAddOrderItemsToGroup(
      items,
      listOrderItems,
      selectGroupItem || selectGroupOrder,
      selectedId,
      selectedVariantId
    );

    setItems(newItems);
  }, [
    selectGroupOrder,
    selectGroupItem,
    selectIdProductGroup,
    selectIdProductOrder,
    selectIdVariantGroup,
    selectIdVariantOrder,
    JSON.stringify(listOrderItems),
  ]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCalcPackageTotal = (packageNode: ITreeItem<IProductGroup>): number => {
    if (!packageNode) return 0;

    const pricingMultiplier = Number(pricingTotal || 1);

    const packageOriginal = listOrderItemsSelected?.find(
      (item) =>
        String(item?.id) === String(packageNode?.data?.inventoryId) &&
        String(item?.variantId || EMPTY_STRING) === String(packageNode?.data?.variantId || EMPTY_STRING)
    );

    const baseRaw = Number(packageNode?.data?.price || 0) * Number(packageNode?.data?.quantity || 1);

    const baseAfterDiscount = handleCalculateDiscountedPrice(
      baseRaw,
      Number(packageNode?.data?.discountRate || 0),
      packageNode?.data?.discountType || EMPTY_STRING
    );

    if (!packageOriginal || !packageOriginal.packageItems?.length) {
      return baseAfterDiscount * pricingMultiplier;
    }

    let extraTotal = 0;

    packageNode.children?.forEach((child) => {
      const childData = child.data;
      if (!childData) return;

      const qty = Number(childData.quantity || 0);
      const price = Number(childData.price || 0);

      if (!childData.isRequired && childData?.isShow) {
        const raw = qty * price;

        const afterDiscount = handleCalculateDiscountedPrice(
          raw,
          Number(childData?.discountRate || 0),
          childData?.discountType || EMPTY_STRING
        );

        extraTotal += afterDiscount;
      }
    });

    return (baseAfterDiscount + extraTotal) * pricingMultiplier;
  };

  const handleCalculateTotal = (items: TreeItems<ITreeItem<IProductGroup>>): number => {
    const calcNodeTotal = (node: ITreeItem<IProductGroup>): number => {
      if (node?.level === 1) {
        const groupTotal = (node.children || []).reduce((sum, child) => sum + calcNodeTotal(child), 0);
        return groupTotal;
      }

      if (node?.data?.type === TypeListInventory.PACKAGE) {
        return handleCalcPackageTotal(node);
      }

      const pricingMultiplier = Number(pricingTotal || 1);

      if (node?.data?.type === TypeListInventory.PRODUCT) {
        const basePrice = Number(node?.data?.price || 0) * Number(node?.data?.quantity || 0);

        const discountedBase = handleCalculateDiscountedPrice(
          basePrice,
          Number(node?.data?.discountRate || 0),
          node?.data?.discountType || EMPTY_STRING
        );

        const childrenTotal = (node.children || []).reduce((sum, child) => sum + calcNodeTotal(child), 0);

        return discountedBase * pricingMultiplier + childrenTotal;
      }

      return Number(node?.data?.price || 0) * Number(node?.data?.quantity || 0) * pricingMultiplier;
    };

    const newSubTotal = items.reduce((sum, node) => sum + calcNodeTotal(node), 0);

    return newSubTotal;
  };

  const updateAvailableInventoryAddOn = (
    listOrderItems: IQuoteListItem[],
    orderAddOns: IQuoteAddOn[]
  ): IQuoteAddOn[] => {
    const variantMap = new Map<string, number>();
    const inventoryMap = new Map<string, number>();

    listOrderItems.forEach((item) => {
      item.addOns?.forEach((ao) => {
        if (!ao.inventoryId) return;
        const inv = ao.inventoryId.toString();

        if (ao.isHasVariant && Array.isArray(ao.availableVariant)) {
          ao.availableVariant.forEach((variant) => {
            const key = `${inv}::${variant.id}`;
            variantMap.set(key, Number(variant.quantity ?? 0));
          });
        } else {
          if (!inventoryMap.has(inv)) inventoryMap.set(inv, Number(ao.availableInventory ?? 0));
        }
      });

      item.packageItems?.forEach((pi) => {
        if (!pi.inventoryId) return;
        const inv = pi.inventoryId.toString();
        const avail = Number(pi.availableInventory ?? 0);
        const vid = pi.variantId?.toString() ?? '';

        if (vid) {
          variantMap.set(`${inv}::${vid}`, avail);
        } else {
          if (!inventoryMap.has(inv)) inventoryMap.set(inv, avail);
        }
      });
    });

    const newAddOns = orderAddOns.map((qa) => {
      const inv = qa.inventoryId ? qa.inventoryId.toString() : '';
      const vid = qa.variantId ? qa.variantId.toString() : '';

      const variantKey = `${inv}::${vid}`;
      if (vid && variantMap.has(variantKey)) {
        return {
          ...qa,
          availableInventory:
            (Number(variantMap.get(variantKey) || 0) + Number(qa?.quantity || 0)).toString() ?? EMPTY_STRING,
        };
      }

      if (inv && inventoryMap.has(inv)) {
        return {
          ...qa,
          availableInventory:
            (Number(inventoryMap.get(inv) || 0) + Number(qa?.quantity || 0)).toString() ?? EMPTY_STRING,
        };
      }

      return { ...qa, availableInventory: '0' };
    });

    return newAddOns;
  };

  const updateAvailableInventoryItem = (listOrderItems: IQuoteListItem[], orderItems: IOrderItem[]): IOrderItem[] => {
    const inventoryMap = new Map<string, string>();

    listOrderItems.forEach((res) => {
      if (!res.id) return;

      const key = `${res.id}_${res.variantId ?? 'null'}`;
      inventoryMap.set(key, res.quantityInventory ?? EMPTY_STRING);
    });

    const newOrderItems = orderItems.map((qi) => {
      if (!qi.inventoryId) {
        return {
          ...qi,
          availableInventory: EMPTY_STRING,
        };
      }

      const key = `${qi.inventoryId}_${qi.variantId ?? 'null'}`;

      return {
        ...qi,
        availableInventory: (Number(inventoryMap.get(key) || 0) + Number(qi?.quantity || 0)).toString() ?? EMPTY_STRING,
      };
    });

    return newOrderItems;
  };

  const handleConvertOrderDataDetailToTreeItem = (
    orderGroups: IOrderGroup[],
    orderItems: IOrderItem[],
    orderAddOns: IQuoteAddOn[]
  ): TreeItems<ITreeItem<IProductGroup>> => {
    const tree: TreeItems<ITreeItem<IProductGroup>> = [];

    const buildAddOns = (
      parentId?: string,
      parentProductId?: string,
      parentType?: string
    ): TreeItems<ITreeItem<IProductGroup>> => {
      const newAddOns = updateAvailableInventoryAddOn(listOrderItems || [], orderAddOns);

      const addOns = newAddOns?.filter((addOn) => addOn?.parentId === parentId);

      if (parentType !== TypeListInventory.PRODUCT) {
        return addOns.map((addOn) => {
          const totalItem = Number(addOn?.price || 0) * Number(addOn?.quantity || 0);
          const totalDiscount = handleCalculateDiscountedPrice(
            Number(totalItem || 0),
            Number(addOn?.discountRate || 0),
            addOn?.discountType || EMPTY_STRING
          );

          return {
            id: uid(),
            level: 3,
            data: {
              ...addOn,
              id: addOn?.id ?? EMPTY_STRING,
              productId: parentProductId ?? EMPTY_STRING,
              addOnId: addOn?.productId ?? EMPTY_STRING,
              idDetail: addOn?.id ?? EMPTY_STRING,
              isAddOn: true,
              total: totalDiscount,
              isRequired: addOn?.isRequired ?? false,
              isShowPrice: addOn?.isShowPrice ?? false,
              isOverbooking: isItemOverbooking(addOn),
            },
            canHaveChildren: false,
          };
        });
      }

      const inventoryIdMap = new Map<string, boolean>();

      return addOns.map((addOn) => {
        const inventoryId = addOn.inventoryId ?? EMPTY_STRING;
        const hasVariant = Boolean(addOn?.variantId && inventoryId);
        const isFirst = hasVariant && !inventoryIdMap.has(inventoryId);

        if (hasVariant) {
          inventoryIdMap.set(inventoryId, true);
        }

        const totalItem = Number(addOn?.price || 0) * Number(addOn?.quantity || 0);
        const totalDiscount = handleCalculateDiscountedPrice(
          Number(totalItem || 0),
          Number(addOn?.discountRate || 0),
          addOn?.discountType || EMPTY_STRING
        );

        return {
          id: uid(),
          level: 3,
          data: {
            ...addOn,
            id: addOn?.id ?? EMPTY_STRING,
            productId: parentProductId ?? EMPTY_STRING,
            addOnId: addOn?.productId ?? EMPTY_STRING,
            isHasVariant: isFirst,
            idRemember: inventoryId,
            idDetail: addOn?.id ?? EMPTY_STRING,
            total: totalDiscount,
            isAddOn: true,
            isRequired: addOn?.isRequired ?? false,
            isShowPrice: addOn?.isShowPrice ?? false,
            isOverbooking: isItemOverbooking(addOn),
          },
          canHaveChildren: false,
        };
      });
    };

    const buildItems = (parentId: string | null): TreeItems<ITreeItem<IProductGroup>> => {
      const newOrderItems = updateAvailableInventoryItem(listOrderItems || [], orderItems);

      const items = newOrderItems.filter((item) => item?.parentId === parentId);

      return items.map((item) => {
        const addOns = buildAddOns(item?.id, item?.productId, item?.type);

        const totalItem = Number(item?.price || 0) * Number(item?.quantity || 0);
        const totalDiscount = handleCalculateDiscountedPrice(
          Number(totalItem || 0),
          Number(item?.discountRate || 0),
          item?.discountType || EMPTY_STRING
        );

        return {
          id: uid(),
          level: 2,
          data: {
            ...item,
            id: item?.id ?? EMPTY_STRING,
            inventoryId: item?.inventoryId ?? EMPTY_STRING,
            name: item?.name,
            note: item?.note ?? EMPTY_STRING,
            price: item?.price ?? EMPTY_STRING,
            discountType: item?.discountType ?? EMPTY_STRING,
            discountRate: item?.discountRate ?? EMPTY_STRING,
            total: totalDiscount,
            category: {
              id: item?.categoryId ?? EMPTY_STRING,
              name: item?.categoryName ?? EMPTY_STRING,
            },
            idDetail: item?.id ?? EMPTY_STRING,
            isOverbooking: isItemOverbooking(item),
          },
          canHaveChildren: true,
          children: addOns,
        };
      });
    };

    orderGroups.forEach((group) => {
      const totalItem = Number(group?.price || 0);
      const totalDiscount = handleCalculateDiscountedPrice(
        Number(totalItem || 0),
        Number(group?.discountRate || 0),
        group?.discountType || EMPTY_STRING
      );

      tree.push({
        id: uid(),
        level: 1,
        data: {
          ...group,
          id: group?.id,
          name: group?.name ?? EMPTY_STRING,
          note: group?.note ?? EMPTY_STRING,
          discountType: group?.discountType ?? EMPTY_STRING,
          discountRate: group?.discountRate ?? EMPTY_STRING,
          price: group?.price ?? EMPTY_STRING,
          total: totalDiscount,
          idDetail: group?.id ?? EMPTY_STRING,
        },
        canHaveChildren: true,
        children: buildItems(group.id ?? EMPTY_STRING),
      });
    });

    tree.push(...buildItems(null));

    return tree;
  };

  const isItemOverbooking = (item: { isOverbooking?: boolean; overbookedQuantity?: number } | undefined): boolean => {
    return Boolean(item?.isOverbooking) || Number(item?.overbookedQuantity ?? 0) > 0;
  };

  const handleFindIdDetailItems = (
    nodes: TreeItems<ITreeItem<IProductGroup>>,
    matcher: (node: ITreeItem<IProductGroup>, parent?: ITreeItem<IProductGroup>) => boolean,
    parent?: ITreeItem<IProductGroup>
  ): string | undefined => {
    for (const node of nodes) {
      if (matcher(node, parent)) {
        return node.data?.idDetail;
      }
      if (node.children?.length) {
        const found = handleFindIdDetailItems(node.children, matcher, node);
        if (found) return found;
      }
    }
    return undefined;
  };

  const handleAddOrderItemsToGroup = (
    prevItems: TreeItems<ITreeItem<IProductGroup>>,
    listOrderItems: IQuoteListItem[],
    selectedGroupId?: string,
    selectedId?: string,
    selectedIdVariant?: string
  ): TreeItems<ITreeItem<IProductGroup>> => {
    const newItems = [...prevItems];

    let indexProduct = 99999;

    const dataFilterLevelOne = newItems.filter((item, index) => {
      const isSameProduct =
        item?.data?.inventoryId === selectedId &&
        (item?.data?.variantId || EMPTY_STRING) === (selectedIdVariant || EMPTY_STRING);

      if (isSameProduct) {
        indexProduct = index;
      }

      return !isSameProduct;
    });

    const newDataFilter = dataFilterLevelOne?.map((item) => {
      if (item?.level === 1 && item?.children?.length) {
        return {
          ...item,
          children: item.children.filter((child, index) => {
            const isSameChild =
              child?.data?.inventoryId === selectedId &&
              (child?.data?.variantId || EMPTY_STRING) === (selectedIdVariant || EMPTY_STRING);

            if (isSameChild) {
              indexProduct = index;
            }

            return !isSameChild;
          }),
        };
      }
      return item;
    });

    const targetGroupId = selectedGroupId;

    const productItems: TreeItems<ITreeItem<IProductGroup>> = [];

    listOrderItems
      .filter((item) => Number(item?.quantity) > 0 && item?.id === selectedId)
      .forEach((item) => {
        const childrenAddOns: TreeItems<ITreeItem<IProductGroup>> = [];

        if (item?.addOns?.length && item?.type !== TypeListInventory.PACKAGE) {
          item?.addOns?.forEach((addOn) => {
            const addOnPrice = Number(addOn?.rentalPrice);
            const addOnQuantity = Number(addOn?.quantity) * Number(item?.quantity);

            const oldIdDetail = handleFindIdDetailItems(
              prevItems,
              (node, parent) =>
                (parent?.data?.inventoryId || EMPTY_STRING) === (item?.id || EMPTY_STRING) &&
                (parent?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING) &&
                node?.data?.addOnId === addOn?.addOnId
            );

            childrenAddOns.push({
              id: uid(),
              level: 3,
              canHaveChildren: false,
              data: {
                id: oldIdDetail || addOn?.id,
                idDetail: oldIdDetail,
                name: addOn?.name,
                inventoryId: addOn?.inventoryId,
                note: item?.note || EMPTY_STRING,
                availableInventory: addOn?.availableInventory,
                price: addOnPrice,
                quantity: addOnQuantity,
                total: addOnPrice * addOnQuantity,
                discountType: DiscountTypeEnum.PERCENTAGE,
                discountRate: EMPTY_STRING,
                laborCost: EMPTY_STRING,
                addOnId: addOn?.addOnId || EMPTY_STRING,
                productId: item?.productId || EMPTY_STRING,
                duration: 0,
                isAddOn: true,
                isHasVariant: addOn?.isHasVariant,
                timeLoading: addOn?.timeLoading || undefined,
                timeUnloading: addOn?.timeUnloading || undefined,
                timeDelivery: addOn?.timeDelivery || undefined,
                timePickup: addOn?.timePickup || undefined,
                isRequired: addOn?.isRequired ?? false,
                isShow: true,
                overbookedQuantity: Number(item?.overbookedQuantity || 0),
              },
            });
          });
        }

        if (item?.packageItems?.length && item?.type === TypeListInventory.PACKAGE) {
          item?.packageItems.forEach((packageItem) => {
            const packagePrice = Number(packageItem?.rentalPrice);
            const packageQuantity = Number(item?.quantity) * Number(packageItem?.quantity);

            const oldIdDetail = handleFindIdDetailItems(
              prevItems,
              (node, parent) =>
                (parent?.data?.inventoryId || EMPTY_STRING) === (item?.id || EMPTY_STRING) &&
                (parent?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING) &&
                node?.data?.inventoryId === packageItem?.inventoryId &&
                (node?.data?.variantId || EMPTY_STRING) === (packageItem?.variantId || EMPTY_STRING)
            );

            childrenAddOns.push({
              id: uid(),
              level: 3,
              canHaveChildren: false,
              data: {
                id: oldIdDetail || packageItem?.id,
                idDetail: oldIdDetail,
                name: packageItem?.name,
                inventoryId: packageItem?.inventoryId || EMPTY_STRING,
                note: item?.note || EMPTY_STRING,
                price: packageItem?.isRequired ? 0 : packagePrice,
                quantity: packageQuantity,
                total: packagePrice * packageQuantity,
                discountType: DiscountTypeEnum.PERCENTAGE,
                discountRate: EMPTY_STRING,
                laborCost: EMPTY_STRING,
                duration: 0,
                timeDelivery: packageItem?.timeDelivery || undefined,
                timePickup: packageItem?.timePickup || undefined,
                timeLoading: packageItem?.timeLoading || undefined,
                timeUnloading: packageItem?.timeUnloading || undefined,
                isAddOn: true,
                variantId: packageItem?.variantId,
                variantName: packageItem?.variantName,
                type: packageItem?.type || EMPTY_STRING,
                availableInventory: packageItem?.availableInventory || '0',
                isRequired: packageItem?.isRequired ?? false,
                isShowPrice: packageItem?.isShowPrice ?? false,
                isShow: packageItem?.isRequired ? true : false,
                overbookedQuantity: Number(item?.overbookedQuantity || 0),
              },
            });
          });
        }

        const oldIdDetail = handleFindIdDetailItems(prevItems, (node, parent) => {
          return (
            (parent?.id || EMPTY_STRING) === (selectedGroupId || EMPTY_STRING) &&
            node?.data?.inventoryId === item?.id &&
            (node?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING)
          );
        });

        productItems.push({
          id: uid(),
          level: 2,
          canHaveChildren: true,
          data: {
            id: oldIdDetail || item?.id,
            idDetail: oldIdDetail,
            inventoryId: item?.id,
            name: item?.name || EMPTY_STRING,
            note: item?.note || EMPTY_STRING,
            price: Number(item?.rentalPrice),
            quantity: Number(item?.quantity),
            total: Number(item?.rentalPrice) * Number(item?.quantity),
            discountType: DiscountTypeEnum.PERCENTAGE,
            discountRate: EMPTY_STRING,
            laborCost: EMPTY_STRING,
            timeDelivery: item?.timeDelivery || undefined,
            timePickup: item?.timePickup || undefined,
            timeLoading: item?.timeLoading || undefined,
            timeUnloading: item?.timeUnloading || undefined,
            duration: 0,
            isAddOn: false,
            variantId: item?.variantId,
            variantName: item?.variantName || EMPTY_STRING,
            availableInventory: item?.quantityInventory || '0',
            type: item?.type || EMPTY_STRING,
            category: item?.category,
            isShow: true,
            overbookedQuantity: Number(item?.overbookedQuantity || 0),
          },
          children: childrenAddOns?.length > 0 ? childrenAddOns : undefined,
        });
      });

    const updatedGroupIndex = newDataFilter?.findIndex((item) => item?.id === targetGroupId && item?.level === 1);

    if (updatedGroupIndex !== -1) {
      const group = newDataFilter[updatedGroupIndex];
      group.children = [
        ...(group?.children?.slice(0, indexProduct) || []),
        ...productItems,
        ...(group?.children?.slice(indexProduct) || []),
      ];
    } else {
      newDataFilter.splice(indexProduct, 0, ...productItems);
    }

    return newDataFilter;
  };

  const handleGetListDataOrderGroups = (items: TreeItems<ITreeItem<IProductGroup>>): IOrderGroup[] => {
    const dataGroupOne: TreeItems<ITreeItem<IProductGroup>> = items?.filter((item) => item?.level === 1);

    const dataOrderGroups: IOrderGroup[] = dataGroupOne.map((item) => {
      const editPrice = Number(item?.data?.price || 0);
      const calculatedPrice = handleGetDataLevelPriceTotal(item)?.price || 0;
      const price = editPrice > 0 ? editPrice : calculatedPrice;

      return {
        id: item?.data?.id || EMPTY_STRING,
        note: item?.data?.note || EMPTY_STRING,
        name: item?.data?.name || EMPTY_STRING,
        discountType: item?.data?.discountType || EMPTY_STRING,
        discountRate: item?.data?.discountRate || EMPTY_STRING,
        price: price,
        total: handleGetDataLevelPriceTotal(item)?.total || 0,
        idDetail: item?.data?.idDetail || EMPTY_STRING,
      };
    });

    return dataOrderGroups;
  };

  const handleGetDataLevelPriceTotal = (item: ITreeItem<IProductGroup>): { price: number; total: number } => {
    if (!item || !item?.children) {
      return { price: 0, total: 0 };
    }

    const calculateRecursive = (nodes: TreeItems<ITreeItem<IProductGroup>>): { price: number; total: number } => {
      let price = 0;
      let total = 0;

      nodes.forEach((node) => {
        price +=
          handleCalculateDiscountedPrice(
            Number(node?.data?.price),
            Number(node?.data?.discountRate),
            node?.data?.discountType || EMPTY_STRING
          ) || 0;

        total += node?.data?.total || 0;

        if (node?.children && node?.children?.length > 0) {
          const childTotals = calculateRecursive(node?.children);
          price += childTotals.price;
          total += childTotals.total;
        }
      });

      return { price, total };
    };

    return calculateRecursive(item?.children);
  };

  const handleGetListDataOrderItems = (treeItems: TreeItems<ITreeItem<IProductGroup>>): IOrderItem[] => {
    const data: IOrderItem[] = [];

    const dataOutsideGroup: TreeItems<ITreeItem<IProductGroup>> = treeItems.filter(
      (item) => item?.level === 2 && !item?.data?.isAddOn
    );

    let indexItemGroup = 0;

    dataOutsideGroup.forEach((item) => {
      const totalItem = Number(item?.data?.price || 0) * Number(item.data?.quantity || 0) * Number(pricingTotal || 1);
      const totalDiscount = handleCalculateDiscountedPrice(
        Number(totalItem || 0),
        Number(item?.data?.discountRate || 0),
        item?.data?.discountType || EMPTY_STRING
      );

      data.push({
        id: item?.data?.id,
        inventoryId: item?.data?.inventoryId || EMPTY_STRING,
        note: item?.data?.note || EMPTY_STRING,
        name: item?.data?.name || EMPTY_STRING,
        quantity: item?.data?.quantity || 0,
        laborCost: item?.data?.laborCost || EMPTY_STRING,
        timeUnloading: item?.data?.timeUnloading || undefined,
        timeLoading: item?.data?.timeLoading || undefined,
        timeDelivery: item?.data?.timeDelivery || undefined,
        timePickup: item?.data?.timePickup || undefined,
        price: item?.data?.price || 0,
        discountType: item?.data?.discountType || EMPTY_STRING,
        discountRate: item?.data?.discountRate || EMPTY_STRING,
        duration: item?.data?.duration || 0,
        additionalHours: item?.data?.additionalHours || 0,
        total: totalDiscount,
        variantId: item?.data?.variantId,
        variantName: item?.data?.variantName,
        category: item?.data?.category,
        idDetail: item?.data?.idDetail,
        isOverbooking: item?.data?.isOverbooking || false,
        availableInventory: item?.data?.availableInventory || '0',
        overbookedQuantity: item?.data?.overbookedQuantity || 0,
      });
    });

    treeItems.forEach((groupItem) => {
      if (groupItem.children && groupItem.level === 1) {
        groupItem.children.forEach((item) => {
          if (item?.level === 2 && !item?.data?.isAddOn) {
            const totalItem =
              Number(item?.data?.price || 0) * Number(item.data?.quantity || 0) * Number(pricingTotal || 1);

            const totalDiscount = handleCalculateDiscountedPrice(
              Number(totalItem || 0),
              Number(item?.data?.discountRate || 0),
              item?.data?.discountType || EMPTY_STRING
            );

            data.push({
              id: item?.data?.id,
              inventoryId: item?.data?.inventoryId || EMPTY_STRING,
              note: item?.data?.note || EMPTY_STRING,
              name: item?.data?.name || EMPTY_STRING,
              quantity: item?.data?.quantity || 0,
              laborCost: item?.data?.laborCost || EMPTY_STRING,
              timeUnloading: item?.data?.timeUnloading || undefined,
              timeLoading: item?.data?.timeLoading || undefined,
              timeDelivery: item?.data?.timeDelivery || undefined,
              timePickup: item?.data?.timePickup || undefined,
              price: item?.data?.price || 0,
              discountType: item?.data?.discountType || EMPTY_STRING,
              discountRate: item?.data?.discountRate || EMPTY_STRING,
              duration: item?.data?.duration || 0,
              additionalHours: item?.data?.additionalHours || 0,
              total: totalDiscount,
              type: item?.data?.type,
              indexGroup: indexItemGroup,
              variantId: item?.data?.variantId,
              variantName: item?.data?.variantName,
              category: item?.data?.category,
              idDetail: item?.data?.idDetail,
              isOverbooking: item?.data?.isOverbooking || false,
              availableInventory: item?.data?.availableInventory || '0',
              overbookedQuantity: item?.data?.overbookedQuantity || 0,
            });
          }
        });

        indexItemGroup += 1;
      }
    });

    return data;
  };

  const handleGetListDataOrderItemsAddOn = (treeItems: TreeItems<ITreeItem<IProductGroup>>): IQuoteAddOn[] => {
    const data: IQuoteAddOn[] = [];

    let orderItemIndex = 0;

    treeItems.forEach((node) => {
      if (node?.level === 2 && !node?.data?.isAddOn) {
        if (node?.children?.length) {
          node.children.forEach((child) => {
            if (child?.level === 3 && child?.data?.isAddOn) {
              const totalItem =
                Number(child?.data?.price || 0) * Number(child?.data?.quantity || 0) * Number(pricingTotal || 1);

              const totalDiscount = handleCalculateDiscountedPrice(
                Number(totalItem || 0),
                Number(child?.data?.discountRate || 0),
                child?.data?.discountType || EMPTY_STRING
              );

              data.push({
                id: child?.data?.id,
                inventoryId: child?.data?.inventoryId || EMPTY_STRING,
                note: child?.data?.note || EMPTY_STRING,
                name: child?.data?.name || EMPTY_STRING,
                quantity: child?.data?.quantity || 0,
                laborCost: child?.data?.laborCost || EMPTY_STRING,
                price: child?.data?.price || 0,
                discountType: child?.data?.discountType || EMPTY_STRING,
                discountRate: child?.data?.discountRate || EMPTY_STRING,
                duration: child?.data?.duration || 0,
                additionalHours: child?.data?.additionalHours || 0,
                total: totalDiscount,
                timeUnloading: child?.data?.timeUnloading,
                timeLoading: child?.data?.timeLoading,
                timeDelivery: child?.data?.timeDelivery,
                timePickup: child?.data?.timePickup,
                indexItem: orderItemIndex,
                variantId: child?.data?.variantId,
                variantName: child?.data?.variantName,
                isRequired: child?.data?.isRequired,
                isShowPrice: child?.data?.isShowPrice ?? false,
                idDetail: child?.data?.idDetail,
                isOverbooking: child?.data?.isOverbooking || false,
                availableInventory: child?.data?.availableInventory || '0',
                idRemember: child?.data?.idRemember || EMPTY_STRING,
                isHasVariant: child?.data?.isHasVariant,
                isShow: child?.data?.isShow,
                overbookedQuantity: child?.data?.overbookedQuantity || 0,
              });
            }
          });
        }

        orderItemIndex++;
      }
    });

    treeItems.forEach((groupNode) => {
      if (groupNode?.level === 1 && groupNode?.children?.length) {
        groupNode.children.forEach((itemNode) => {
          if (itemNode?.level === 2 && !itemNode?.data?.isAddOn) {
            if (itemNode?.children?.length) {
              itemNode.children.forEach((child) => {
                if (child?.level === 3 && child?.data?.isAddOn) {
                  const totalItem =
                    Number(child?.data?.price || 0) * Number(child?.data?.quantity || 0) * Number(pricingTotal || 1);

                  const totalDiscount = handleCalculateDiscountedPrice(
                    Number(totalItem || 0),
                    Number(child?.data?.discountRate || 0),
                    child?.data?.discountType || EMPTY_STRING
                  );

                  data.push({
                    id: child?.data?.id,
                    inventoryId: child?.data?.inventoryId || EMPTY_STRING,
                    note: child?.data?.note || EMPTY_STRING,
                    name: child?.data?.name || EMPTY_STRING,
                    quantity: child?.data?.quantity || 0,
                    laborCost: child?.data?.laborCost || EMPTY_STRING,
                    price: child?.data?.price || 0,
                    discountType: child?.data?.discountType || EMPTY_STRING,
                    discountRate: child?.data?.discountRate || EMPTY_STRING,
                    duration: child?.data?.duration || 0,
                    additionalHours: child?.data?.additionalHours || 0,
                    total: totalDiscount,
                    timeUnloading: child?.data?.timeUnloading,
                    timeLoading: child?.data?.timeLoading,
                    timeDelivery: child?.data?.timeDelivery,
                    timePickup: child?.data?.timePickup,
                    indexItem: orderItemIndex,
                    variantId: child?.data?.variantId,
                    variantName: child?.data?.variantName,
                    isRequired: child?.data?.isRequired,
                    isShowPrice: child?.data?.isShowPrice ?? false,
                    idDetail: child?.data?.idDetail,
                    isOverbooking: child?.data?.isOverbooking || false,
                    availableInventory: child?.data?.availableInventory || '0',
                    idRemember: child?.data?.idRemember || EMPTY_STRING,
                    isHasVariant: child?.data?.isHasVariant,
                    isShow: child?.data?.isShow,
                    overbookedQuantity: child?.data?.overbookedQuantity || 0,
                  });
                }
              });
            }

            orderItemIndex++;
          }
        });
      }
    });

    return data;
  };

  const handleAddGroup = () => {
    setItems([
      ...items,
      {
        id: uid(),
        level: 1,
        canHaveChildren: true,
        data: {
          id: uid(),
          name: `Group ${indexGroup}`,
          note: EMPTY_STRING,
          discountType: DiscountTypeEnum.PERCENTAGE,
          discountRate: EMPTY_STRING,
          price: 0,
          total: 0,
        },
      },
    ]);

    setIndexGroup((prev) => prev + 1);
  };

  const handleRemoveGroup = () => {
    const newItems: TreeItems<ITreeItem<IProductGroup>> = [];
    const deleteIds: string[] = [];

    items.forEach((item) => {
      if (item?.level === 1) {
        if (item?.data?.idDetail && !isNaN(Number(item?.data?.idDetail))) {
          deleteIds?.push(String(item?.data?.idDetail));
        }

        if (item?.children) {
          item?.children.forEach((child: ITreeItem<IProductGroup>) => {
            newItems.push({
              ...child,
              id: uid(),
              level: 2,
              canHaveChildren: true,
            });
          });
        }
      }

      if (item?.level !== 1) {
        newItems.push(item);
      }
    });

    if (deleteIds.length > 0) {
      setValue('deleteItemIds', [...(deleteItemIds || []), ...deleteIds]);
    }

    setItems(newItems);
  };

  const handleItemsChanged = (
    items: TreeItems<ITreeItem<IProductGroup>>,
    reason: ItemChangedReason<ITreeItem<IProductGroup>>
  ) => {
    if (reason.type === 'dropped') {
      const draggedItem = reason.draggedItem;
      const droppedToParent = reason.droppedToParent;

      if (draggedItem?.level === 1 && droppedToParent) return;

      if (draggedItem?.level === 2 && droppedToParent?.level === 2) return;

      if (draggedItem?.level === 3) return;

      setItems(items);
    } else {
      setItems(items);
    }
  };

  const handleGroupByCategory = () => {
    const categoryGroup =
      orderItems
        ?.map((item) => item?.category)
        .filter((item) => item?.id && item?.name)
        .filter(
          (item, index, self) =>
            index === self?.findIndex((category) => category?.id === item?.id && category?.name === item?.name)
        ) || [];

    const dataItemsGroup: TreeItems<ITreeItem<IProductGroup>> = [];

    // Remove Group Level 1
    items.forEach((item) => {
      if (item?.level === 1 && item?.children) {
        item?.children.forEach((child) => {
          dataItemsGroup?.push({ ...child });
        });
      } else if (item?.level !== 1) {
        dataItemsGroup?.push(item);
      }
    });

    const newItems: TreeItems<ITreeItem<IProductGroup>> = [];

    // Create Group To Category
    categoryGroup.forEach((category) => {
      const groupChildren = dataItemsGroup?.filter(
        (child) => child?.level === 2 && child?.data?.category?.id === category?.id
      );

      if (groupChildren.length > 0) {
        newItems.push({
          id: uid(),
          level: 1,
          canHaveChildren: true,
          data: {
            id: uid(),
            name: category?.name,
            note: EMPTY_STRING,
            price: 0,
            discountType: DiscountTypeEnum.PERCENTAGE,
            discountRate: EMPTY_STRING,
            total: 0,
          },
          children: groupChildren.map((child) => ({
            ...child,
            level: 2,
          })),
        });
      }
    });

    // Add Date Level 2 No Category
    const dataNoCategory = dataItemsGroup.filter((child) => !child?.data?.category?.id);

    newItems.push(...dataNoCategory);

    setItems(newItems);
  };

  // const handleLog = () => {
  //   console.log(JSON.stringify(items, null, 2));
  // };
  //#endregion Handle Function

  return (
    <Stack className={cx('container')}>
      <Stack direction={'row'} alignItems={'center'}>
        <Typography className={cx('title')}>{t('form_groups_title_products')}</Typography>
      </Stack>

      <Stack className={cx('viewAction')}>
        <Stack width={416} gap={4} direction={'row'} alignItems={'center'}>
          {/* <Button
            variant='outlined'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={t('common_alt_img')} />}
            onClick={handleLog}
          >
            {t('form_groups_title_new_group')}
          </Button> */}

          <Button
            variant='outlined'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={t('common_alt_img')} />}
            onClick={handleAddGroup}
          >
            {t('form_groups_title_new_group')}
          </Button>

          {/* <Button variant='contained' className={cx('buttonGroup')} onClick={handleGroupByCategory}>
            {t('form_groups_title_group_by_category')}
          </Button> */}

          <Button
            variant='contained'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconClose} alt={t('common_alt_img')} />}
            onClick={handleRemoveGroup}
          >
            {t('form_groups_title_remove_groups')}
          </Button>
        </Stack>
      </Stack>

      <GroupContext.Provider value={{ items, setItems }}>
        <DndContext>
          <Box className={cx('viewTable')}>
            <Table stickyHeader>
              <TableHead className={cx('tableHead')}>
                <TableRow>
                  <TableCell width={'20%'} className={cx('titleHead')}>
                    {t('form_groups_title_group_name')}
                  </TableCell>

                  <TableCell width={'15%'} className={cx('titleHead')}>
                    {t('form_groups_title_discount')}
                  </TableCell>

                  <TableCell width={'12%'} className={cx('titleHead')}>
                    {t('form_groups_title_price')}
                  </TableCell>

                  <TableCell width={'8%'} className={cx('titleHead')}>
                    {t('form_groups_title_labor_cost')}
                  </TableCell>

                  <TableCell width={'18%'} className={cx('titleHead')} align='center'>
                    {t('form_groups_title_quantity')}
                  </TableCell>

                  <TableCell width={'12%'} className={cx('titleHead')}>
                    {t('form_groups_title_total')}
                  </TableCell>
                  <TableCell width={'5%'} className={cx('titleHead')}>
                    <Box width={'24px'} height={'24px'} />
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>

            <Stack>
              <SortableTree
                items={items}
                onItemsChanged={handleItemsChanged}
                TreeItemComponent={ItemGroupOrder}
                indentationWidth={25}
              />
            </Stack>
          </Box>
        </DndContext>
      </GroupContext.Provider>

      <Stack alignItems={'flex-end'} gap={'14px'} className={cx('viewFooter')}>
        <Stack direction={'row'} gap={2} width={'306px'} alignItems={'center'} justifyContent={'space-between'}>
          <Box className={cx('titleLabel')}>{t('form_groups_title_product_subtotal')}</Box>
          <Box className={cx('titleValue')}>
            {formatCurrencyUSD({ value: Number(productSubtotal || 0) - Number(discountRate || 0), showDecimal: true })}
          </Box>
        </Stack>

        <Stack direction={'row'} gap={2} width={'306px'} alignItems={'center'} justifyContent={'space-between'}>
          <Box className={cx('titleLabel')}>{t('form_groups_title_labor_cost_title')}</Box>
          <Box className={cx('titleValue')}> {formatCurrencyUSD({ value: laborCost, showDecimal: true })}</Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default GroupsOrderForm;
